import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import * as moment from 'moment'
import { catchError, finalize, map, mapTo, switchMap, take, tap } from 'rxjs/operators'
import {
  createPlaceFieldsForm,
  PlaceFieldsFormComponent,
  setPlaceFieldsFormData,
} from '../../template-drawer/components/place-fields-form/place-fields-form.component'
import {
  createSendContractInfoForm,
  setSendContractInfosFormData,
} from '../../template-drawer/components/send-contract-info-form/send-contract-info-form.component'
import { SendContractService } from '../send-contract.service'
import {
  createSignersForm,
  OneDefaultSigner,
  setSignersWithRoleFormData,
  SignerItemFormDataWithRole,
} from '../components/signers-with-role/signers-with-role.component'
import { combineLatest, Observable, of } from 'rxjs'
import * as lodash from 'lodash'
import { getContractSigners, SignerOptionItem } from '../components/signer-input-base.component'
import { PreviewTemplateDrawerService } from '../../template-drawer/preview-template/preview-template-drawer.service'
import {
  Contract,
  ContractService,
  ContractSignerType,
  ContractStatus,
  ContractTemplate,
  CreateContract,
  CreateContractComplete,
  getFillBeforeSendingFields,
  selectContractTemplateViewById,
  SignFieldDef,
  TemplateTypes,
} from '@tokeet-frontend/signature'
import { ActionFailed, Destroyable, Toaster, untilDestroy, validateForm } from '@tokeet-frontend/tv3-platform'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import { AuthService } from '@tv3/services/auth.service'
import { GuestService } from '@tv3/store/guest/guest.service'
import { SignDrawerService } from '@tv3/containers/documents/sign/sign-drawer.service'
import { selectAllCustomCodes } from '@tv3/store/custom-codes/custom-code.selectors'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStepper } from '@angular/material/stepper'

enum SendTemplateSteps {
  AddSigner,
  PlaceFields,
  DocumentDetails,
}

@Component({
  selector: 'app-send-template-drawer',
  templateUrl: './send-template-drawer.component.html',
  styleUrls: ['./send-template-drawer.component.scss'],
})
export class SendTemplateDrawerComponent extends Destroyable implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper
  steps = SendTemplateSteps
  get currentStep() {
    return this.stepper?.selectedIndex
  }

  @ViewChild('placeFieldsFormCom') placeFieldsFormCom: PlaceFieldsFormComponent

  signersForm = createSignersForm(this.fb)
  fieldsForm = createPlaceFieldsForm(this.fb)
  contractInfoForm = createSendContractInfoForm(this.fb)

  templateTypes = TemplateTypes
  template: ContractTemplate
  customCodes: CustomCode[]

  isSending = false

  onlyMeSigner = false

  constructor(
    private dialog: MatDialogRef<SendTemplateDrawerComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private toaster: Toaster,
    private authService: AuthService,
    public sendTemplateService: SendContractService,
    private contractService: ContractService,
    private guestService: GuestService,
    private previewDrawer: PreviewTemplateDrawerService,
    private signDrawer: SignDrawerService,
    @Inject(MAT_DIALOG_DATA) private data: { templateId?: string; signer?: OneDefaultSigner }
  ) {
    super()
  }

  getFieldValue(form: FormGroup, key: string) {
    return form.get(key)?.value
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectContractTemplateViewById(this.data.templateId)), untilDestroy(this))
      .subscribe((template) => {
        this.template = template
        setSignersWithRoleFormData(this.fb, this.signersForm, template, this.data.signer)
        setPlaceFieldsFormData(this.fieldsForm, template)
        setSendContractInfosFormData(this.contractInfoForm, template)
      })
    this.store.pipe(select(selectAllCustomCodes), untilDestroy(this)).subscribe((items) => {
      this.customCodes = items
    })

    this.signersForm.valueChanges
      .pipe(
        map((items: SignerItemFormDataWithRole[]) => {
          return items.length === 1 && items[0].id === this.authService.user.id
        }),
        untilDestroy(this)
      )
      .subscribe((onlyMeSigner) => {
        this.onlyMeSigner = onlyMeSigner
      })
  }

  close() {
    this.dialog.close()
  }

  nextStep() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }
    this.stepper.next()
  }

  hasPrev() {
    return this.currentStep > SendTemplateSteps.AddSigner
  }

  prevStep() {
    this.stepper.previous()
  }

  onPreview() {
    const { font, font_size } = this.fieldsForm.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    this.previewDrawer.open({
      type: this.template.type,
      fields,
      pdfFileUrl: this.template.pdf_file_url,
      defaultFontSize: font_size,
      defaultFont: font,
    })
  }

  validateStep() {
    switch (this.currentStep) {
      case SendTemplateSteps.AddSigner:
        return validateForm(this.signersForm)
      case SendTemplateSteps.PlaceFields:
        return this.placeFieldsFormCom.isTemplateReady()
      case SendTemplateSteps.DocumentDetails:
        return validateForm(this.contractInfoForm)
    }
  }

  showValidationMessage() {
    switch (this.currentStep) {
      case SendTemplateSteps.AddSigner:
        this.toaster.warning($localize`Correct the fields shown in red before going to next.s`)
        return
      case SendTemplateSteps.PlaceFields:
        return this.toaster.warning($localize`To process to next step, please add Signature field for each role.`)
      case SendTemplateSteps.DocumentDetails:
        return
    }
  }

  getFillBeforeSendingFields(fieldsForm: FormGroup) {
    const { fields } = fieldsForm.getRawValue()
    return getFillBeforeSendingFields(fields)
  }

  isBookingRequired(service: SendContractService, type: TemplateTypes, fields: SignFieldDef[]) {
    return service.isBookingRequired(type, fields)
  }

  getContractPayload(signers: SignerOptionItem[]) {
    const currentUser = this.authService.currentUser
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    const { name, title, message } = this.contractInfoForm.getRawValue()

    const contractPayload = this.contractService.createContractFromTemplate(this.template, {
      name,
      title,
      message,
      fields,
      account: currentUser.account,
      user_id: currentUser.id,
      signers: getContractSigners(signers),
    })

    const { account, id } = currentUser
    contractPayload.account = `${account}`
    contractPayload.user_id = id
    //
    if (this.template?.expired_days > 0) {
      contractPayload.expired_at = moment.utc().add(this.template.expired_days, 'days').endOf('day').unix()
    }

    return contractPayload
  }

  resolveSigners(): Observable<SignerOptionItem[]> {
    const signers: SignerItemFormDataWithRole[] = this.signersForm.getRawValue()

    return combineLatest(
      lodash.map(signers, (signer: SignerItemFormDataWithRole) => {
        if (!signer.save || signer.email === signer.data?.primaryEmail) {
          return of(signer)
        }
        return this.guestService.create({ name: signer.name, email: signer.email }).pipe(
          map((guest): SignerOptionItem => {
            return {
              name: signer.name,
              email: signer.email,
              id: guest.id,
              type: ContractSignerType.Guest,
              role: signer.role,
              order: signer.order,
              data: guest,
              id_photo_required: signer.id_photo_required,
              selfie_photo_required: signer.selfie_photo_required,
            }
          }),
          catchError(() => of(signer))
        )
      })
    )
  }

  send(silent = false) {
    this.isSending = true
    return this.resolveSigners().pipe(
      map((signers) => this.getContractPayload(signers)),
      switchMap((contractPayload: Contract) => {
        const { inquiry } = this.contractInfoForm.getRawValue()

        return this.sendTemplateService.tryToFillWithTokeetData(contractPayload, inquiry).pipe(mapTo(contractPayload))
      }),
      tap((contractPayload) => this.store.dispatch(CreateContract({ payload: contractPayload, silent }))),
      switchMap(() =>
        this.actions.pipe(
          ofType(CreateContractComplete, ActionFailed),
          tap(() => (this.isSending = false)),
          take(1),
          ofType(CreateContractComplete)
        )
      ),
      finalize(() => (this.isSending = false))
    )
  }

  onSignIt() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }
    this.send(true)
      .pipe(
        switchMap(({ item }) => {
          if (item.status === ContractStatus.Pending) {
            return this.signDrawer.open(item.contract_id, item.signers?.[0].role).afterClosed()
          } else {
            return of(true)
          }
        })
      )
      .subscribe(() => {
        this.toaster.success($localize`Document is signed successfully.`)
        this.dialog.close()
      })
  }

  onSend() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }

    this.send().subscribe(() => {
      this.dialog.close()
    })
  }
}
